<script setup lang="ts">
import {
  BlockBanIcon,
  BoxComponent,
  ConfirmationPopup,
  type Creator,
  CreatorStatusEnum,
  FlexComponent,
  TypographyComponent,
} from '@metapic-apps/shared'
import { computed, ref } from 'vue'

import { useChangeCreatorsStatus } from '@/api'
import CloseIcon from '@/assets/icons/remove.svg?component'
import ThumbsUpIcon from '@/assets/icons/thumb.svg?component'
import { notification } from '@/lib/helpers'
import { ButtonComponent, LoadingComponent } from '@/ui-library'

type CreatorStatusActionType = 'approve' | 'ban'

const props = defineProps<{ selectedCreators: Record<number, Creator>; isLimitReached?: boolean }>()
const emits = defineEmits<{
  close: []
  completed: [{ action: CreatorStatusActionType; changedAmount: number }]
}>()

const isConfirmationOpen = ref(false)
const pendingAction = ref<CreatorStatusActionType | null>(null)
const { changeCreatorsStatus, isLoading } = useChangeCreatorsStatus()

const selectedUserIds = computed(() => Object.keys(props.selectedCreators).map((creatorId) => Number(creatorId)))
const selectedAmount = computed(() => Object.keys(props.selectedCreators).length)

const hasUnacceptedCreators = computed(() =>
  Object.values(props.selectedCreators).some((creator) => creator.status !== CreatorStatusEnum.Approved),
)
const hasUnsuspendedCreators = computed(() =>
  Object.values(props.selectedCreators).some((creator) => creator.status !== CreatorStatusEnum.Banned),
)

const confirmationTitle = computed(() => (pendingAction.value === 'approve' ? 'Accept creators' : 'Suspend creators'))
const confirmationDescription = computed(() => {
  if (pendingAction.value === 'approve') {
    return `Are you sure you want to accept the ${selectedAmount.value} selected creators?`
  }

  return `Are you sure you want to suspend the ${selectedAmount.value} selected creators?`
})
const confirmationSubmitText = computed(() => (pendingAction.value === 'approve' ? 'Accept all' : 'Suspend all'))
const confirmationSubmitVariant = computed(() => (pendingAction.value === 'approve' ? 'primary' : 'danger'))

const openConfirmation = (action: CreatorStatusActionType) => {
  pendingAction.value = action
  isConfirmationOpen.value = true
}

const handleSubmit = async () => {
  if (!pendingAction.value || !selectedUserIds.value.length) {
    return
  }

  const action = pendingAction.value
  isConfirmationOpen.value = false

  const changedAmount = await changeCreatorsStatus(selectedUserIds.value, action)

  if (typeof changedAmount !== 'number') {
    notification.error(action === 'approve' ? 'Failed to accept selected creators' : 'Failed to suspend selected creators')
    return
  }

  emits('completed', { action, changedAmount })
  emits('close')
}
</script>
<template>
  <BoxComponent :pt="8" :pb="8" :pl="16" :pr="16" class="creators-bulk-action-popup">
    <LoadingComponent v-if="isLoading" :size="32" color="var(--inverted)" />
    <FlexComponent v-else direction="column" align="center" :gap="4">
      <TypographyComponent v-if="isLimitReached" variant="title-medium" color="var(--inverted)">
        Selection limit of {{ selectedAmount }} reached
      </TypographyComponent>
      <BoxComponent :pl="8">
        <FlexComponent align="center" :gap="16">
          <TypographyComponent variant="body-medium" color="#FFFFFF7A"> {{ selectedAmount }} selected </TypographyComponent>
          <FlexComponent :gap="8">
            <ButtonComponent
              v-if="hasUnacceptedCreators"
              variant="text"
              class="creators-bulk-action-popup__button"
              @click="openConfirmation('approve')"
            >
              <template #iconLeft>
                <ElIcon :size="18">
                  <ThumbsUpIcon class="creators-bulk-action-popup__icon" />
                </ElIcon>
              </template>
              Accept
            </ButtonComponent>
            <ButtonComponent
              v-if="hasUnsuspendedCreators"
              variant="text"
              class="creators-bulk-action-popup__button creators-bulk-action-popup__button--danger"
              @click="openConfirmation('ban')"
            >
              <template #iconLeft>
                <ElIcon :size="18">
                  <BlockBanIcon class="creators-bulk-action-popup__icon" />
                </ElIcon>
              </template>
              Suspend
            </ButtonComponent>
            <ButtonComponent variant="text" icon class="creators-bulk-action-popup__button" @click="$emit('close')">
              <CloseIcon class="creators-bulk-action-popup__icon" />
            </ButtonComponent>
          </FlexComponent>
        </FlexComponent>
      </BoxComponent>
    </FlexComponent>
  </BoxComponent>
  <ConfirmationPopup
    v-model="isConfirmationOpen"
    :title="confirmationTitle"
    :description="confirmationDescription"
    :submit-btn-text="confirmationSubmitText"
    :submit-btn-variant="confirmationSubmitVariant"
    @on-submit="handleSubmit"
  />
</template>

<style scoped lang="scss">
.creators-bulk-action-popup {
  position: fixed;
  left: 50%;
  bottom: 16px;
  transform: translateX(-50%);

  display: flex;
  justify-content: center;
  align-items: center;

  min-height: 52px;
  min-width: 260px;
  border-radius: 16px;
  background: var(--primary);
  box-shadow: 4px 4px 32px 0 var(--button-hover);
  z-index: var(--z-index-8);
}

:deep(.creators-bulk-action-popup__button.button) {
  color: var(--inverted);
  padding: 8px;
  border-radius: 8px;

  &:hover {
    background: #ffffff1f;
  }
}

:deep(.creators-bulk-action-popup__button--danger.button) {
  &:hover {
    background: #d600007a;
  }
}

:deep(.creators-bulk-action-popup__icon) {
  path {
    fill: var(--inverted);
  }
}
</style>
